import { Injectable } from '@angular/core';
import { PagoState } from 'src/app/store/state/pago.state';
import { IDatalayerEvent } from 'src/app/shared/utils/IDatalayerEvent.utils';
import { PushDatalayersGoogleTagManager } from '@segurosvoluntarios_vel/commons-services';
import { environment } from 'src/environments/environment';
import { ErrorPago } from 'src/app/pago/models/ErrorPago.model';
import { ModalError } from 'src/app/pago/models/ModalError.model';
import { Ruta } from 'src/app/pago/enums/Ruta.enum';
import { IDatalayerErrorAttribute } from 'src/app/pago/const/DatalayerErrorAttribute.const';
import { MensajeError } from 'src/app/pago/enums/MensajeError.enum';
import { ButtonTextModal } from 'src/app/pago/enums/ButtonTextModal.enum';
import { CodigoErrorNegocio } from 'src/app/pago/models/CodigoErrorNegocio.model';
import { Datalayer } from 'src/app/pago/const/Datalayer.enum';

@Injectable()
export abstract class ManagerErrorPagoService {
  static readonly TIME_OUT = '408';
  static readonly SIN_RESPUESTA_A_TIEMPO = '504';
  static readonly MAXIMO_INTENTOS = '409';

  static getError(state: PagoState, errors: ErrorPago[]) {
    let errorPago: ModalError;
    const urlRedirectValidacion = `/${state.tenant}/${Ruta.VALIDACION}/${state.pago.idPago}`;

    let datalayerAttribute: IDatalayerErrorAttribute;

    if (errors) {
      const error = errors[0];
      switch (error.codigo) {
        case ManagerErrorPagoService.TIME_OUT:
        case ManagerErrorPagoService.SIN_RESPUESTA_A_TIEMPO:
          errorPago = this.buildError({
            description: MensajeError.VALIDANDO_PAGO,
            buttonText: ButtonTextModal.ACEPTAR,
            buttonAction: urlRedirectValidacion,
          });

          datalayerAttribute = {
            etiqueta: MensajeError.VALIDANDO_PAGO,
            codigoError: error.codigo,
            mensajeInterno: error.mensaje,
          };

          break;
        case ManagerErrorPagoService.MAXIMO_INTENTOS:
          errorPago = this.buildError({
            description: MensajeError.MAXIMO_INTENTOS,
            buttonText: ButtonTextModal.REGRESAR,
          });

          datalayerAttribute = {
            etiqueta: MensajeError.MAXIMO_INTENTOS,
            codigoError: error.codigo,
            mensajeInterno: error.mensaje,
          };

          break;
        case CodigoErrorNegocio.PAGO_APROBADO:
          errorPago = this.buildError({
            description: `${error.codigo}-${error.mensaje}`,
            buttonText: ButtonTextModal.ACEPTAR,
            buttonAction: urlRedirectValidacion,
          });

          datalayerAttribute = {
            etiqueta: CodigoErrorNegocio.PAGO_APROBADO,
            codigoError: error.codigo,
            mensajeInterno: error.mensaje,
          };

          break;
        default:
          errorPago = this.buildError({
            description: MensajeError.SERVICIO_NO_DISPONIBLE,
            buttonText: ButtonTextModal.ACEPTAR,
          });

          datalayerAttribute = {
            etiqueta: MensajeError.SERVICIO_NO_DISPONIBLE,
            codigoError: error.codigo,
            mensajeInterno: error.mensaje,
          };

          break;
      }
    } else {
      errorPago = this.buildError({
        description: MensajeError.VALIDANDO_PAGO,
        buttonText: ButtonTextModal.ACEPTAR,
        buttonAction: urlRedirectValidacion,
      });

      datalayerAttribute = {
        etiqueta: MensajeError.VALIDANDO_PAGO,
        codigoError: Datalayer.DEFAULT_ERROR_CODE,
        mensajeInterno: Datalayer.DEFAULT_MENSAJE_INTERNO,
      };
    }
    state.error = errorPago;
    this.buildDatalayerError(state, datalayerAttribute);

    return state;
  }

  static getErrorOtroMedioDePago(state: PagoState, errors: ErrorPago[]) {
    let errorPago: ModalError;
    const error = errors[0];

    let datalayerAttribute: IDatalayerErrorAttribute;

    if (error && error.mensaje.includes(CodigoErrorNegocio.PAGO_PENDIENTE)) {
      errorPago = this.buildError({
        description: MensajeError.PAGO_PENDIENTE,
        buttonText: ButtonTextModal.ACEPTAR,
        buttonAction: state.pago?.urlRetorno || environment.suraPath,
      });

      datalayerAttribute = {
        etiqueta: MensajeError.PAGO_PENDIENTE,
        codigoError: error.codigo,
        mensajeInterno: error.mensaje,
      };
    } else {
      errorPago = this.buildError({
        description: MensajeError.SERVICIO_INDISPONIBLE,
        buttonText: ButtonTextModal.ACEPTAR,
      });

      datalayerAttribute = {
        etiqueta: MensajeError.SERVICIO_INDISPONIBLE,
        codigoError:
          error.codigo || Datalayer.DEFAULT_ERROR_CODE_SERVICIO_INDISPONIBLE,
        mensajeInterno: error.mensaje || Datalayer.DEFAULT_MENSAJE_INTERNO,
      };
    }
    state.error = errorPago;
    this.buildDatalayerError(state, datalayerAttribute);

    return state;
  }

  static getErrorEstadoPago(state: PagoState, errors: ErrorPago[]) {
    let errorPago: ModalError;
    const error = errors[0];

    switch (error.codigo) {
      case CodigoErrorNegocio.ID_PAGO_INCORRECTO:
      case CodigoErrorNegocio.TIEMPO_SESION_EXPIRADO:
        errorPago = this.buildError({
          description: error.mensaje,
          buttonText: ButtonTextModal.ACEPTAR,
          buttonAction: state.pago?.urlRetorno || environment.suraPath,
        });
        break;
      default:
        errorPago = this.buildError({
          description: MensajeError.ERROR_CONSULTANDO_ESTADO_PAGO,
          buttonText: ButtonTextModal.ACEPTAR,
          buttonAction: window.location.href,
        });
        break;
    }
    state.error = errorPago;
    return state;
  }

  static getErrorConfiguracionDePago(state: PagoState, errors: ErrorPago[]) {
    let errorPago: ModalError;
    const error = errors[0];

    switch (error.codigo) {
      case CodigoErrorNegocio.PAGO_APROBADO_CONSULTANDO:
      case CodigoErrorNegocio.ID_PAGO_INCORRECTO:
      case CodigoErrorNegocio.TIEMPO_SESION_EXPIRADO:
        errorPago = this.buildError({
          description: error.mensaje,
          buttonText: ButtonTextModal.ACEPTAR,
          buttonAction: state.pago?.urlRetorno || environment.suraPath,
        });
        break;
      default:
        errorPago = this.buildError({
          description: error.mensaje,
          buttonText: ButtonTextModal.ACEPTAR,
        });
        break;
    }
    state.error = errorPago;

    if (error.codigo) {
      const datalayerAttribute = {
        etiqueta: error.mensaje || MensajeError.SERVICIO_INDISPONIBLE,
        codigoError:
          error.codigo || Datalayer.DEFAULT_ERROR_CODE_SERVICIO_INDISPONIBLE,
        mensajeInterno: error.mensaje || Datalayer.DEFAULT_MENSAJE_INTERNO,
      };
      this.buildDatalayerError(state, datalayerAttribute);
    }

    return state;
  }

  private static buildError({
    active = true,
    validarEstadoPago = true,
    description,
    buttonText,
    buttonAction,
  }: ModalError): ModalError {
    return {
      active,
      validarEstadoPago,
      description,
      buttonText,
      buttonAction,
    };
  }

  private static buildDatalayerError(
    state: PagoState,
    { etiqueta, codigoError, mensajeInterno }
  ): void {
    const dataLayerError: IDatalayerEvent = {
      event: Datalayer.EVENT_CUADRO_ALERTA,
      products: {
        name: state.productNameFromUrl || state.pago.idProducto,
        category: Datalayer.CATEGORIA_TRANSVERSAL,
        accion: window.location.pathname,
        etiqueta: etiqueta,
        [Datalayer.ID_PAGO]: state.pago.idPago,
        [Datalayer.CODIGO_ERROR]: codigoError,
        [Datalayer.MENSAJE_INTERNO]: mensajeInterno,
      },
    };
    PushDatalayersGoogleTagManager.pushObject(dataLayerError);
  }
}
